import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAppCache } from "../../hooks/Cachecontext";

const InventoryContext = createContext();

// IDs to exclude (disabled stores: Warehouse, Central Kitchen, LIWA, Catering, Kiosk)
const EXCLUDED_STORE_IDS = new Set([
  "60a8edb84fa7cd28f240661e", // Al Farooj Fresh - Warehouse
  "60a8eeb341bb70768c66ee3c", // Al Farooj Fresh - Central Kitchen
  "6145986916e3af4b7e7a5785", // Al Farooj Fresh - LIWA
  "619e1f0f19ee3b40ed5119a8", // Al Farooj Fresh - Catering ( Al Wasl Club )
  "6204d056fa0f3b04ce24cb3a", // Al Farooj Fresh - Kiosk
]);

// Filter helper — assumes API returns an object keyed by store_id
// If keyed by store name, swap to: Object.fromEntries(
//   Object.entries(data).filter(([, v]) => !EXCLUDED_STORE_IDS.has(v?.store_id))
// )
const filterExcludedStores = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  return Object.fromEntries(
    Object.entries(data).filter(([key]) => !EXCLUDED_STORE_IDS.has(key))
  );
};

export const useInventoryContext = () => {
  const context = useContext(InventoryContext);
  if (!context) throw new Error("useInventoryContext must be used within an InventoryProvider");
  return context;
};

export const InventoryProvider = ({ children }) => {
  const baseUrl        = process.env.REACT_APP_BASE_URL;
  const { get, set }   = useAppCache();

  const [inventoryContext, setInventoryContext] = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [dateFrom, setDateFrom]                 = useState("");
  const [dateTo, setDateTo]                     = useState("");

  const fetchInventory = useCallback(async () => {
    const cacheKey = `alfaroojfresh:inventory:${dateFrom}:${dateTo}`;
    const cached   = get(cacheKey);
    if (cached) {
      setInventoryContext(cached); // already filtered (was filtered before caching)
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo)   params.dateTo   = dateTo;

      const { data } = await axios.get(
        `${baseUrl}/api/api-farooj/recipe-food-cost`,
        { params }
      );

      const filtered = filterExcludedStores(data || []); // 👈 filter before caching & setting state
      const ttl      = dateFrom || dateTo ? 2 * 60 * 1000 : 5 * 60 * 1000;

      set(cacheKey, filtered, ttl);
      setInventoryContext(filtered);
    } catch (err) {
      console.error("Error fetching inventory:", err);
      setError("Failed to fetch inventory.");
      setInventoryContext([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, dateFrom, dateTo, get, set]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  return (
    <InventoryContext.Provider
      value={{
        inventoryContext,
        loading,
        error,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        refetch: fetchInventory,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
};